import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldAlert } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const BREAKER_IMG = "https://images.unsplash.com/photo-1576446470246-499c738d1c8e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
export const ABOUT_TEXT = "PRICEHUB is a convenient digital destination for discovering and accessing electrical industry price lists from multiple brands in one place.";
export const DISCLAIMER_TEXT = "PRICEHUB is an independent price-list directory. All brand names and trademarks belong to their respective owners. Price lists are provided for informational purposes. Always verify current pricing with the respective company or authorised distributor.";

export function AboutSection({ showLink = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
      <div>
        <Reveal>
          <p className="mb-5 flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-ph-accent"><span className="h-1.5 w-1.5 rounded-full bg-ph-lime" /> About PriceHub</p>
          <h2 className="font-display text-4xl font-extrabold leading-[0.95] tracking-[-0.03em] text-ph-ink sm:text-5xl lg:text-6xl">One search.<br /><span className="text-ph-muted">Every price list.</span></h2>
          <p className="mt-7 text-lg leading-relaxed text-ph-ink/85" data-testid="about-text">{ABOUT_TEXT}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-[1.5rem] border border-ph-violet/30 bg-ph-violet/[0.07] p-6" data-testid="about-disclaimer">
            <p className="flex items-center gap-2 font-mono-ph text-xs font-semibold uppercase tracking-[0.18em] text-ph-accent"><ShieldAlert className="h-4 w-4" /> Disclaimer</p>
            <p className="mt-3 text-sm leading-relaxed text-ph-ink/85">{DISCLAIMER_TEXT}</p>
            <p className="mt-3 text-sm leading-relaxed text-ph-ink/85">Only documents that are authorised for distribution or otherwise lawfully shared are published.</p>
          </div>
          {showLink && (
            <Link to="/about" data-testid="about-read-more" className="group mt-8 inline-flex items-center gap-2 font-semibold text-ph-ink">
              <span className="border-b-2 border-ph-lime pb-0.5">More about PriceHub</span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
          )}
        </Reveal>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="relative">
        <div className="ph-clip-frame relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-[#101625]">
          <motion.img style={{ y, scale: 1.18 }} src={BREAKER_IMG} alt="Row of modular circuit breakers" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_25%,transparent_20%,rgba(16,22,37,0.6)_75%)]" />
        </div>
        <div className="absolute -bottom-6 left-4 rounded-2xl border border-ph-line bg-ph-surface/85 px-5 py-4 shadow-[0_20px_40px_-20px_rgba(16,22,37,0.5)] backdrop-blur-xl sm:-left-6">
          <p className="font-mono-ph text-[11px] uppercase tracking-[0.18em] text-ph-muted">Formats</p>
          <p className="font-display text-2xl font-extrabold text-ph-ink">PDF <span className="text-ph-accent">+</span> Excel</p>
        </div>
      </motion.div>
    </div>
  );
}
